import PropTypes from 'prop-types';

import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import * as Styled from './SubscriptionPrice.style';

function SubscriptionPrice({ subscription, price, message, promotionalItem, useAltStyles, oneYearCommitment }) {
  const { formatMessage } = useIntl();
  const { payment = [] } = subscription;
  const paymentStatus = payment[0]?.paymentStatus || '';
  const priceDisplay =
    price === undefined ? formatMessage({ id: 'dashboard.subscriptions.price.included' }) : `$${price} `;

  return (
    <Stack
      gap={0.5}
      sx={(theme) => ({
        minWidth: '9.75rem',
        alignItems: 'flex-end',
        [theme.breakpoints.down('md')]: {
          alignItems: 'flex-start',
        },
      })}
    >
      <Stack direction="row" alignItems="center" gap={0.5}>
        {price === undefined && (
          <Text size="md" weight="bold">
            {priceDisplay}
          </Text>
        )}

        {price !== undefined && !promotionalItem && (
          <Text size="md" weight="bold">
            {priceDisplay}
          </Text>
        )}

        {price !== undefined && promotionalItem && (
          <>
            <Styled.StrikethroughPrice size="md" weight="bold">
              {priceDisplay}
            </Styled.StrikethroughPrice>
            <Text size="md" weight="bold">
              {`$${promotionalItem.netAmount} `}
            </Text>
          </>
        )}

        {price !== undefined && (
          <Text size="md" color="secondary">
            {` / ${
              subscription?.period === 'monthly'
                ? formatMessage({ id: 'dashboard.subscriptions.price.periodMonth' })
                : formatMessage({ id: 'dashboard.subscriptions.price.periodYear' })
            }`}
          </Text>
        )}
      </Stack>

      {message !== null && (
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={(theme) => ({
            ...(useAltStyles && { color: 'error.main', fontWeight: 'bold' }),
            ...(paymentStatus === 'failed' && { color: 'error.main' }),
            textAlign: 'right',
            [theme.breakpoints.down('md')]: {
              textAlign: 'left',
            },
          })}
        >
          {paymentStatus === 'failed' && (
            <Text size="sm" color="error">
              ⚠
            </Text>
          )}
          <Text
            size="sm"
            data-testid="payment-and-message"
            sx={{
              ...(useAltStyles && { fontWeight: 'bold', color: 'error.main' }),
            }}
          >
            {message}
          </Text>
        </Stack>
      )}

      {oneYearCommitment && (
        <Text size="sm" color="secondary" data-testid="one-year-commitment">
          <FormattedMessage id="dashboard.subscriptions.price.oneYearCommitment" />
        </Text>
      )}
    </Stack>
  );
}

SubscriptionPrice.propTypes = {
  subscription: PropTypes.shape({
    period: PropTypes.string,
    payment: PropTypes.arrayOf(
      PropTypes.shape({
        paymentStatus: PropTypes.string,
      }),
    ),
  }).isRequired,
  price: PropTypes.number,
  message: PropTypes.string,
  promotionalItem: PropTypes.shape({
    couponUuid: PropTypes.string,
    expirationDate: PropTypes.string,
    orderBillingItemUuid: PropTypes.string,
    netAmount: PropTypes.number,
    couponDetails: PropTypes.shape({
      amount: PropTypes.number,
    }),
  }),
  useAltStyles: PropTypes.bool,
  oneYearCommitment: PropTypes.bool,
};

export default SubscriptionPrice;
